import { Link, useLocation } from "react-router-dom";
import { PieChart, Wallet, Wrench, Zap, ShieldAlert, TrendingUp, Bot, Coins, Users, Bitcoin, LifeBuoy } from "lucide-react";

/**
 * AdminTabsNav — single source of truth for the admin nav.
 * Used on /admin/withdrawals (tabs as URL search params), /admin/orphans,
 * and /admin/perps. Mobile-first grid.
 *
 * Props:
 *  - active: "stats" | "withdrawals" | "recovery" | "swaps" | "orphans" | "perps"
 *  - onTabChange?: (tab) => void   — when present, the first 4 tabs become
 *      buttons that just call this callback (used INSIDE /admin/withdrawals
 *      to switch internal state). When absent, they become Links to
 *      /admin/withdrawals?tab=X (used FROM /admin/orphans + /admin/perps).
 *  - pendingCount?: number — withdrawals badge
 */
export default function AdminTabsNav({ active, onTabChange, pendingCount = 0 }) {
  const tabs = [
    { key: "stats", label: "Stats", icon: PieChart, activeBg: "bg-blue-600" },
    { key: "withdrawals", label: "Withdrawals", icon: Wallet, activeBg: "bg-blue-600" },
    { key: "recovery", label: "Recovery", icon: Wrench, activeBg: "bg-orange-600" },
    { key: "swaps", label: "Swaps", icon: Zap, activeBg: "bg-amber-600" },
  ];

  const baseClass =
    "relative flex flex-col items-center justify-center gap-1 px-1 py-2 rounded-lg text-[10px] sm:text-xs font-medium transition-colors";

  return (
    <div className="grid grid-cols-3 sm:grid-cols-9 gap-1.5 mb-6">
      {tabs.map((t) => {
        const isActive = active === t.key;
        const Icon = t.icon;
        const content = (
          <>
            <Icon className="w-4 h-4" />
            <span>{t.label}</span>
            {t.key === "withdrawals" && pendingCount > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[9px] px-1 rounded-full font-bold leading-4"
                data-testid="admin-tab-withdrawals-badge"
              >
                {pendingCount}
              </span>
            )}
          </>
        );
        const className = `${baseClass} ${
          isActive ? `${t.activeBg} text-white` : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`;
        if (onTabChange) {
          return (
            <button
              key={t.key}
              onClick={() => onTabChange(t.key)}
              className={className}
              data-testid={`admin-tab-${t.key}`}
            >
              {content}
            </button>
          );
        }
        return (
          <Link
            key={t.key}
            to={`/admin/withdrawals?tab=${t.key}`}
            className={className}
            data-testid={`admin-tab-${t.key}`}
          >
            {content}
          </Link>
        );
      })}
      <Link
        to="/admin/orphans"
        className={`${baseClass} ${
          active === "orphans"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-orphans"
      >
        <ShieldAlert className="w-4 h-4" />
        <span>Orphans</span>
      </Link>
      <Link
        to="/admin/wallets"
        className={`${baseClass} ${
          active === "wallets"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-wallets"
      >
        <Users className="w-4 h-4" />
        <span>Wallets</span>
      </Link>
      <Link
        to="/admin/bots"
        className={`${baseClass} ${
          active === "bots"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-bots"
      >
        <Bot className="w-4 h-4" />
        <span>Bots</span>
      </Link>
      <Link
        to="/admin/flip"
        className={`${baseClass} ${
          active === "flip"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-flip"
      >
        <Coins className="w-4 h-4" />
        <span>Flip</span>
      </Link>
      <Link
        to="/admin/perps"
        className={`${baseClass} ${
          active === "perps"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-perps"
      >
        <TrendingUp className="w-4 h-4" />
        <span>Perps</span>
      </Link>
      <Link
        to="/admin/bond-tracker"
        className={`${baseClass} ${
          active === "bonds"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-bonds"
      >
        <Bitcoin className="w-4 h-4" />
        <span>Bonds</span>
      </Link>
      <Link
        to="/admin/support"
        className={`${baseClass} ${
          active === "support"
            ? "bg-orange-600 text-white"
            : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
        }`}
        data-testid="admin-tab-support"
      >
        <LifeBuoy className="w-4 h-4" />
        <span>Support</span>
      </Link>
    </div>
  );
}

// Re-export the hook for callers that need the URL tab param
export function useAdminTabFromUrl(defaultTab = "stats") {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get("tab");
  const valid = ["stats", "withdrawals", "recovery", "swaps"];
  return valid.includes(tab) ? tab : defaultTab;
}
